/**
 * Language button: a globe with the current language code, opening a list of
 * the supported languages in their own names. Mounted into every element with
 * the class `lang-slot` (header, sign-in screens). Picking a language calls
 * i18n `setLocale` (remembered on this device, saved to the account when
 * signed in, then the page reloads with its state kept).
 */
import { LOCALES, getLocale, setLocale, t } from '../../../shared/i18n/index.js';

const GLOBE = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>';

let deps = { saveToAccount: null, reload: null };

function close(root) {
  root.querySelector('.lang-menu')?.remove();
  root.querySelector('.lang-btn')?.setAttribute('aria-expanded', 'false');
}

function open(root) {
  const current = getLocale();
  const menu = document.createElement('ul');
  menu.className = 'lang-menu';
  menu.setAttribute('role', 'listbox');
  menu.setAttribute('aria-label', t('lang.label'));
  for (const { code, name } of LOCALES) {
    const item = document.createElement('li');
    item.setAttribute('role', 'option');
    item.setAttribute('aria-selected', String(code === current));
    item.tabIndex = -1;
    item.dataset.locale = code;
    item.lang = code;
    item.textContent = name;
    menu.appendChild(item);
  }
  root.appendChild(menu);
  root.querySelector('.lang-btn').setAttribute('aria-expanded', 'true');
  (menu.querySelector('[aria-selected="true"]') || menu.firstElementChild).focus();

  const choose = async (code) => {
    close(root);
    if (code) await setLocale(code, deps);
  };
  menu.addEventListener('click', (e) => {
    const item = e.target.closest('[data-locale]');
    if (item) choose(item.dataset.locale);
  });
  menu.addEventListener('keydown', (e) => {
    const items = [...menu.children];
    const i = items.indexOf(document.activeElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      items[(i + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length].focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      choose(document.activeElement?.dataset?.locale);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close(root);
      root.querySelector('.lang-btn').focus();
    }
  });
}

function render(slot) {
  const current = LOCALES.find((l) => l.code === getLocale()) || LOCALES[0];
  slot.innerHTML = '';
  const root = document.createElement('div');
  root.className = 'lang-picker';
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'lang-btn';
  btn.setAttribute('aria-haspopup', 'listbox');
  btn.setAttribute('aria-expanded', 'false');
  btn.setAttribute('aria-label', `${t('lang.label')}: ${current.name}`);
  btn.title = t('lang.label');
  btn.innerHTML = `${GLOBE}<span class="lang-code">${current.code.toUpperCase()}</span>`;
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (root.querySelector('.lang-menu')) close(root);
    else open(root);
  });
  root.appendChild(btn);
  slot.appendChild(root);
}

/** Pick a language from elsewhere in the app (mobile account sheet). */
export function chooseLanguage(code) {
  return setLocale(code, deps);
}

/**
 * Mount the button into every `.lang-slot`.
 * @param {{saveToAccount?: (code: string) => Promise<unknown>|null, reload?: () => Promise<boolean>}} options
 *   saveToAccount stores the choice on the account (only when signed in);
 *   reload reloads the app keeping its state (storage reloadApp).
 */
export function mountLanguageButtons(options = {}) {
  deps = { ...deps, ...options };
  document.querySelectorAll('.lang-slot').forEach(render);
  if (!mountLanguageButtons.listening) {
    mountLanguageButtons.listening = true;
    document.addEventListener('click', (e) => {
      document.querySelectorAll('.lang-picker').forEach((root) => {
        if (!root.contains(e.target)) close(root);
      });
    });
  }
}
